import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DialogContent } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import {
  DialogActions, IconButton,
} from '@material-ui/core';
import useAttachment
  from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/MessageAttachments/MessageAttachment/useAttachment';
import Tooltip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Tooltip';
import DownloadIcon from '@material-ui/icons/CloudDownloadOutlined';
import SendToSecureFilesIcon from '@material-ui/icons/FolderSharedOutlined';
import ShowThreadIcon from '@material-ui/icons/FeaturedPlayListOutlined';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import Spacer from 'Components/UI/Spacer';
import classnames from 'classnames';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Menu from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Menu';
import MenuItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/MenuItem';
import ListItemText from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemText';
import useMobileLayout from 'Hooks/useMobileLayout';
import { PreviewItem } from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer/components/PreviewItem';
import fr from 'facet-rest';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    height: '80vh',
    justifyContent: 'center',
  },
  desktopRoot: {
  },
  mobileRoot: {
  },
  paperRoot: {
    height: '100%',
    width: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    overflow: 'hidden',
    transition: 'all .2s ease',
    verticalAlign: 'middle',
  },
  dialogPaper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  contentRoot: {
    display: 'flex',
  },
  imagePreviewHeader: {

  },
  file: {
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.common.white,
    flexGrow: 1,
  },
  preview: {
    backgroundColor: 'transparent',
    display: 'flex',
    '& html': {
      background: 'blue',
    },
  },
  actions: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  actionButton: {
    borderRadius: 0,
    padding: theme.spacing(0.5, 1),
    color: theme.palette.facet.primaryGray,
    // padding: theme.spacing(0.25, 0),
    height: '100%',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.facet.darkGray,
      backgroundColor: theme.palette.facet.lightGray,
    },
  },
  action: {
    marginRight: theme.spacing(1),
  },
  navButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  navButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.facet.primaryGray}`,
  },
  primaryActionsContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
  },
  overflowMenu: {
    width: '225px',
  },
}));

const AttachmentPreview = ({
  attachmentResource,
  onNext,
  onPrev,
  extraActions,
}) => {
  const classes = useStyles();
  const {
    onDownload,
    onSendToSecureFiles,
    sendingToSecureFiles,
    onShowThread,
  } = useAttachment(attachmentResource);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const isMobile = useMobileLayout();

  return (
    <>
      <DialogContent
        className={classnames(
          classes.root,
          {
            [classes.desktopRoot]: !isMobile,
            [classes.mobileRoot]: isMobile,
          },
        )}
        test={`file-viewer-preview-${attachmentResource.link('self').url()}`}
      >
        <PreviewItem
          presentsAsDownloadable={attachmentResource.withRelationship(fr.relationships.presentsAsDownloadable.name, () => true, () => false)}
          attachmentName={attachmentResource.attr('name')}
          attachmentHref={attachmentResource.withRelationship(
            fr.relationships.download.name,
            (downloadRel) => downloadRel.link('related').url(),
            () => null,
          )}
          hasResource={!!attachmentResource}
        />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <div className={classes.navButtons}>
          {onPrev && (
            <Tooltip placement="top" title="Previous">
              <IconButton className={classes.navButton} onClick={onPrev} test={'file-viewer-prev-button'}>
                <ChevronLeftIcon fontSize={'large'} />
              </IconButton>
            </Tooltip>
          )}
          <Spacer />
          {onNext && (
            <Tooltip placement="top" title="Next">
              <IconButton className={classes.navButton} onClick={onNext} test={'file-viewer-next-button'}>
                <ChevronRightIcon fontSize={'large'} />
              </IconButton>
            </Tooltip>
          )}
        </div>
        <div className={classes.primaryActionsContainer}>
          {onDownload && (
            <Tooltip placement="top" title="Download">
              <IconButton className={classnames(classes.actionButton, classes.action)} onClick={onDownload} test={'file-viewer-download-button'}>
                <DownloadIcon fontSize={'default'} />
              </IconButton>
            </Tooltip>
          )}
          {onShowThread && (
            <Tooltip placement="top" title="Show Thread">
              <IconButton className={classnames(classes.actionButton, classes.action)} onClick={onShowThread} test={'file-viewer-show-thread-button'}>
                <ShowThreadIcon fontSize={'default'} />
              </IconButton>
            </Tooltip>
          )}
          {onSendToSecureFiles && (
            <Tooltip placement="top" title="Send to secure files">
              <IconButton
                className={classnames(classes.actionButton, classes.action)}
                onClick={onSendToSecureFiles}
                disabled={sendingToSecureFiles}
                test={'file-viewer-send-to-secure-files-button'}
              >
                <SendToSecureFilesIcon fontSize={'default'} fontWeight={200} />
              </IconButton>
            </Tooltip>
          )}
          {extraActions.length > 0 && (
            <>
              <IconButton
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'extra-actions-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                test={'file-viewer-overflow-menu-button'}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="extra-actions-menu"
                MenuListProps={{
                  'aria-labelledby': 'long-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  className: classes.overflowMenu,
                }}
              >
                {extraActions.map((action) => {
                  return (
                    <MenuItem onClick={action.onClick} disabled={action.disabled} test={`file-viewer-overflow-menu-${action.testName}`}>
                      <ListItemText>{action.prompt}</ListItemText>
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          )}
        </div>
      </DialogActions>
    </>
  );
};

export { AttachmentPreview };
